import { useState, useMemo } from "react";
import {
  Plus, Star, StarOff, Download, ChevronDown, ChevronUp, Filter, X,
  MessageCircle, Eye, MapPin, Truck, DoorOpen, Zap, Phone, Paperclip, Target } from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import { CLIENT_STAGES, FLEET_SIZES, REGIONS } from "../constants";
import { fmt, exportCSV, waLink, getToday, fmtDate } from "../utils";
import { contatos, contatosNoDia, contatosNaSemana } from "../lib/metas";

const ATIVOS = ["ganho"];
const FORA = ["perdido", "descartado"];
const NEGOC = CLIENT_STAGES.map((s) => s.id).filter((id) => ![...ATIVOS, ...FORA].includes(id));

const PERIODOS = [
  { id: "all", label: "Qualquer data" }, { id: "hoje", label: "Hoje" },
  { id: "7", label: "Últimos 7 dias" }, { id: "30", label: "Últimos 30 dias" },
];
const diasAtras = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };

export default function Clients({ clients, setClients, today, search, stageFilter, setStageFilter, setShowNewClient, setSelClient, onAdesao, role = "comercial", userId, metas = [], onRegistroRapido, setShowNewInteraction }) {
  const gerencial = role === "gerente" || role === "admin";
  // Contagem de contatos de cliente — o espelho do painel da Rede. Conta cada
  // conversa/export lançado no dia. Para gerência é o time inteiro; para o
  // comercial, o próprio.
  const metaClientes = useMemo(() => {
    const lista = contatos(clients, gerencial ? {} : { userId });
    const doDia = contatosNoDia(lista, today || getToday());
    return {
      feitos: doDia.length,
      comProva: doDia.filter((c) => c.comprovado).length,
      semana: contatosNaSemana(lista, today || getToday()).length,
    };
  }, [clients, gerencial, userId, today]);
  const [statusView, setStatusView] = useState("all"); // all|ganho|negociacao|fora
  const [regionFilter, setRegionFilter] = useState("all");
  const [fleetFilter, setFleetFilter] = useState("all");
  const [periodoFilter, setPeriodoFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recente");
  const [showFilters, setShowFilters] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const toggleStage = (id) => setExpandedStage((p) => (p === id ? null : id));

  const kpis = useMemo(() => {
    const mes = getToday().slice(0, 7);
    let ganho = 0, negoc = 0, fora = 0, mesCount = 0, pipeline = 0;
    for (const c of clients) {
      if (c.stage === "ganho") ganho++;
      else if (FORA.includes(c.stage)) fora++;
      else { negoc++; pipeline += parseInt(c.estimatedValue) || 0; }
      if ((c.createdAt || "").slice(0, 7) === mes) mesCount++;
    }
    return { total: clients.length, ganho, negoc, fora, mesCount, pipeline };
  }, [clients]);

  const regionOptions = useMemo(() => Array.from(new Set((clients || []).map((c) => c.region).filter(Boolean))).sort(), [clients]);

  const filtered = useMemo(() => {
    setPage(1);
    const texto = (q || search || "").toLowerCase().trim();
    const cutoff = periodoFilter === "hoje" ? getToday() : periodoFilter !== "all" ? diasAtras(Number(periodoFilter)) : null;
    const list = clients.filter((c) => {
      if (statusView === "ganho" && c.stage !== "ganho") return false;
      if (statusView === "negociacao" && !NEGOC.includes(c.stage)) return false;
      if (statusView === "fora" && !FORA.includes(c.stage)) return false;
      if (stageFilter !== "all" && c.stage !== stageFilter) return false;
      if (regionFilter !== "all" && c.region !== regionFilter) return false;
      if (fleetFilter !== "all" && c.fleetSize !== fleetFilter) return false;
      if (cutoff) { const cd = c.createdAt || ""; if (periodoFilter === "hoje" ? cd !== cutoff : cd < cutoff) return false; }
      if (texto) { const hay = `${c.company} ${c.contact || ""} ${c.email || ""} ${c.cnpj || ""}`.toLowerCase(); if (!hay.includes(texto)) return false; }
      return true;
    });
    list.sort((a, b) => {
      const av = `${a.createdAt || ""}${a.id}`, bv = `${b.createdAt || ""}${b.id}`;
      return sort === "recente" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
    return list;
  }, [clients, statusView, stageFilter, regionFilter, fleetFilter, periodoFilter, q, search, sort]);

  const paginated = useMemo(() => { const s = (page - 1) * pageSize; return filtered.slice(s, s + pageSize); }, [filtered, page, pageSize]);

  const handleExport = () => {
    const data = filtered.map((c) => ({
      Empresa: c.company, Contato: c.contact, Cargo: c.title, Telefone: c.phone, Email: c.email,
      Região: c.region, Frota: FLEET_SIZES.find((f) => f.id === c.fleetSize)?.label || "",
      Estágio: CLIENT_STAGES.find((s) => s.id === c.stage)?.label || "", "Valor Estimado": c.estimatedValue || 0,
      "Próxima Ação": c.nextAction || "", CNPJ: c.cnpj || "", Criado: fmtDate(c.createdAt), "Último Contato": fmtDate(c.lastContact),
    }));
    exportCSV(data, "clientes_instasolutions");
  };

  const limpar = () => { setStageFilter("all"); setRegionFilter("all"); setFleetFilter("all"); setPeriodoFilter("all"); setQ(""); };
  const filtrosAtivos = stageFilter !== "all" || regionFilter !== "all" || fleetFilter !== "all" || periodoFilter !== "all" || q;

  const sel = { padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12, background: "#fff", cursor: "pointer", fontFamily: "inherit", width: "100%" };
  const inp = { ...sel, cursor: "text" };
  const toggleStar = (id, ev) => { ev.stopPropagation(); setClients((p) => p.map((x) => x.id === id ? { ...x, starred: !x.starred } : x)); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <GuideBanner
        title="Frente 1 — Captação de Clientes com Frota"
        text="Empresas com frota. Cadastre, registre TODOS os contatos (com a conversa exportada) e atualize o estágio conforme a negociação avança."
      />

      {/* Contagem de contatos do dia — cada conversa/export lançado conta */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", borderLeft: "4px solid #0d9488", padding: "12px 14px" }}>
        <Target size={16} color="#0d9488" />
        <div style={{ minWidth: 150 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
            {metaClientes.feitos} {metaClientes.feitos === 1 ? "contato" : "contatos"}{gerencial ? " do time" : ""} hoje
          </div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>
            {gerencial ? "Todos os contatos e exports de cliente no dia." : "Seus contatos de cliente no dia."} Cada conversa lançada conta.
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "#475569", marginLeft: "auto" }}>
          <span title="Contatos com conversa/print anexado"><Paperclip size={10} style={{ verticalAlign: "-1px" }} /> {metaClientes.comProva}/{metaClientes.feitos} com prova</span>
          <span>Semana: <strong style={{ color: "#0f172a" }}>{metaClientes.semana}</strong></span>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <KpiCard label="Total de clientes" value={kpis.total} color="#0d9488" active={statusView === "all"} onClick={() => setStatusView("all")} />
        <KpiCard label="Ganhos" value={kpis.ganho} color="#059669" active={statusView === "ganho"} onClick={() => setStatusView("ganho")} />
        <KpiCard label="Em negociação" value={kpis.negoc} color="#3b82f6" active={statusView === "negociacao"} onClick={() => setStatusView("negociacao")} />
        <KpiCard label="Perdidos/Descartados" value={kpis.fora} color="#f59e0b" active={statusView === "fora"} onClick={() => setStatusView("fora")} />
        <KpiCard label="Pipeline (R$)" value={fmt(kpis.pipeline)} color="#7c3aed" small />
      </div>

      {/* Barra de ações */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowFilters((v) => !v)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: `1px solid ${filtrosAtivos ? "#0d9488" : "#d1d5db"}`, background: filtrosAtivos ? "#f0fdfa" : "#fff", color: filtrosAtivos ? "#0f766e" : "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Filter size={13} /> Filtros{filtrosAtivos ? " •" : ""}
          </button>
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ ...sel, width: "auto" }}>
            <option value="recente">Mais recentes</option>
            <option value="antigo">Mais antigos</option>
          </select>
          <button onClick={() => setShowPipeline((v) => !v)} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            {showPipeline ? "Ocultar" : "Ver"} pipeline
          </button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={handleExport} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Download size={13} /> CSV
          </button>
          {/* A adesão é a porta: cliente de licitação, indicação ou prospecção
              nasce por aqui e já sai com as ativações agendadas. */}
          {onAdesao && (
            <button onClick={() => onAdesao(null)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #7c3aed", background: "#f5f3ff", color: "#6d28d9", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 6 }}>
              <DoorOpen size={14} /> Registrar adesão
            </button>
          )}
          {onRegistroRapido && (
            <button onClick={onRegistroRapido} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", color: "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }} title="Anotou o contato? Registre o essencial agora e complete depois">
              <Zap size={13} /> Registro rápido
            </button>
          )}
          <button onClick={() => setShowNewClient(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "8px 14px", borderRadius: 7, border: "none", background: "#0d9488", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={14} /> Novo Lead
          </button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <div><Lbl>Buscar (empresa, CNPJ)</Lbl><input style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Empresa, CNPJ, e-mail..." /></div>
            <div><Lbl>Estágio</Lbl><select style={sel} value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}><option value="all">Todos</option>{CLIENT_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></div>
            <div><Lbl>Região</Lbl><select style={sel} value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}><option value="all">Todas</option>{(regionOptions.length ? regionOptions : REGIONS).map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
            <div><Lbl>Frota</Lbl><select style={sel} value={fleetFilter} onChange={(e) => setFleetFilter(e.target.value)}><option value="all">Todas</option>{FLEET_SIZES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select></div>
            <div><Lbl>Data de cadastro</Lbl><select style={sel} value={periodoFilter} onChange={(e) => setPeriodoFilter(e.target.value)}>{PERIODOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select></div>
          </div>
          {filtrosAtivos && (
            <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>{filtered.length} resultado(s)</span>
              <button onClick={limpar} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}><X size={12} /> Limpar filtros</button>
            </div>
          )}
        </div>
      )}

      {/* Pipeline colapsável */}
      {showPipeline && (
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Pipeline Clientes — Clique no estágio</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CLIENT_STAGES.filter((s) => !FORA.includes(s.id) && s.id !== "ganho").map((s) => {
              const count = (clients || []).filter((c) => c.stage === s.id).length;
              const isExpanded = expandedStage === s.id;
              return (
                <div key={s.id} style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div onClick={() => toggleStage(s.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, cursor: "pointer", background: isExpanded ? s.color + "15" : "#f8fafc", border: `2px solid ${isExpanded ? s.color : "#e2e8f0"}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{s.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: s.color, marginLeft: "auto" }}>{count}</span>
                    {isExpanded ? <ChevronUp size={12} color="#64748b" /> : <ChevronDown size={12} color="#64748b" />}
                  </div>
                </div>
              );
            })}
          </div>
          {CLIENT_STAGES.filter((s) => expandedStage === s.id).map((s) => {
            const stageClients = (clients || []).filter((c) => c.stage === s.id);
            return (
              <div key={s.id} style={{ marginTop: 10, padding: 10, background: "#f8fafc", borderRadius: 8, border: `1px solid ${s.color}30` }}>
                {stageClients.length === 0 ? <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 12 }}>Nenhum cliente neste estágio</div> : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                    {stageClients.map((c) => (
                      <div key={c.id} onClick={() => setSelClient(c)} style={{ background: "#fff", borderRadius: 7, padding: 10, cursor: "pointer", border: "1px solid #e5e7eb" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{c.company}</div>
                        <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{c.contact || "Sem contato"}</div>
                        {c.estimatedValue > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginTop: 3 }}>{fmt(c.estimatedValue)}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 10 }}>
        {paginated.length === 0 && <div style={{ gridColumn: "1/-1", padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12 }}>Nenhum cliente encontrado com os filtros atuais</div>}
        {paginated.map((c) => {
          const stage = CLIENT_STAGES.find((s) => s.id === c.stage);
          const overdue = c.nextActionDate && c.nextActionDate < today && !["ganho", "perdido", "descartado"].includes(c.stage);
          const wa = waLink(c.whatsapp || c.phone);
          return (
            <div key={c.id} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb", borderLeft: `4px solid ${stage?.color || "#ccc"}` }}>
              <div onClick={() => setSelClient(c)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
                  <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                    <button onClick={(ev) => toggleStar(c.id, ev)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}>
                      {c.starred ? <Star size={13} fill="#f59e0b" color="#f59e0b" /> : <StarOff size={13} color="#d1d5db" />}
                    </button>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.company}</div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>{c.contact}{c.title ? ` — ${c.title}` : ""}</div>
                    </div>
                  </div>
                  <Badge color={stage?.color}>{stage?.label}</Badge>
                </div>
                <div style={{ fontSize: 11, color: "#475569", marginBottom: 3, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <span><Truck size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{FLEET_SIZES.find((f) => f.id === c.fleetSize)?.label || "—"}</span>
                  <span><MapPin size={10} style={{ verticalAlign: "-1px", marginRight: 3 }} />{c.region || "—"}</span>
                </div>
                {c.estimatedValue > 0 && <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{fmt(c.estimatedValue)}</div>}
                {c.nextAction && <div style={{ fontSize: 10, color: overdue ? "#ef4444" : "#94a3b8", fontWeight: overdue ? 700 : 400, marginTop: 3 }}>{overdue ? "ATRASADO — " : "→ "}{c.nextAction.slice(0, 45)}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                {setShowNewInteraction && (
                  <button onClick={() => setShowNewInteraction({ type: "client", id: c.id })} style={{ ...actBtn(false), background: "#0d9488", color: "#fff", borderColor: "#0d9488" }} title="Registrar contato (anexe a conversa exportada)"><Phone size={12} /> Registrar contato</button>
                )}
                <button onClick={() => setSelClient(c)} style={actBtn(false)} title="Abrir"><Eye size={12} /> Detalhe</button>
                <button onClick={() => wa && window.open(wa, "_blank")} disabled={!wa} style={{ ...actBtn(!wa), color: wa ? "#059669" : "#cbd5e1", borderColor: wa ? "#a7f3d0" : "#e2e8f0" }} title={wa ? "WhatsApp" : "Sem telefone"}><MessageCircle size={12} /></button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb" }}>
        <Pagination page={page} totalItems={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, active, onClick, small }) {
  return (
    <div onClick={onClick} style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", border: `1px solid ${active ? color : "#e5e7eb"}`, borderLeft: `4px solid ${color}`, cursor: onClick ? "pointer" : "default", boxShadow: active ? `0 0 0 1px ${color}` : "none" }}>
      <div style={{ fontSize: small ? 16 : 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</div>
    </div>
  );
}
function Lbl({ children }) { return <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{children}</div>; }
const actBtn = (disabled) => ({ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: disabled ? "#cbd5e1" : "#475569", fontSize: 11, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer" });
